import HiddenInput from "@/components/form/components/HiddenInput";
import LabelWithTooltip from "@/components/form/labels/LabelWithTooltip";
import AntIcon from "@/components/icons/AntIcon";
import ContentWrapper from "@/components/wrappers/ContentWrapper";
import { setErrorsToForm } from "@/utils/formUtils";
import { useObjectUrl } from "@/utils/hooksUtils";
import { getDefaultLogo } from "@/utils/utils";
import { validations } from "@/utils/validationUtils";
import { Button, Card, Form, Input, Result, Typography } from "antd";
import classNames from "classnames";
import { Trans, useTranslation } from "react-i18next";
import { useSearchParams } from "react-router";
import { useConfirmEmailSignature, useDownloadOriginalDocumentThroughEmail } from "../../../common/api/queries";
import { TopAgentType } from "../../../common/enums";
import { useBranding } from "../../branding/store";
import { TokenAccessDocSignature } from "../types";
import styles from "./ConfirmDocumentSignContainer.module.scss";

export const ConfirmDocumentSignContainer = () => {
  const { t } = useTranslation();

  const branding = useBranding();
  const logoUrl = branding?.logoResourcePath ? `/images/${branding.logoResourcePath}` : getDefaultLogo("dark");

  const [urlParams] = useSearchParams();
  const token = urlParams.get("token");
  const clientName = urlParams.get("clientName");
  const documentName = urlParams.get("documentName");
  const topAgentType = urlParams.get("topAgentType");
  const smsRequired = urlParams.get("smsRequired");

  const { data, isError, error, isFetching } = useDownloadOriginalDocumentThroughEmail({ token: token ?? "" });

  const url = useObjectUrl(data);

  const confirmEmailSignature = useConfirmEmailSignature();

  const [form] = Form.useForm<TokenAccessDocSignature>();

  return (
    <ContentWrapper className="center-align margin-bottom-large">
      <img className={styles.appLogo} src={logoUrl} alt="App Logo" />

      {isFetching && (
        <Result
          status="info"
          icon={<AntIcon type="loading" />}
          title={<Trans i18nKey="documentSign.helpers.loading" />}
        />
      )}

      {isError && <Result status="error" title={error.message} />}

      {confirmEmailSignature.isSuccess && !isError && (
        <Result
          status="success"
          title={<Trans i18nKey="documentSign.titles.documentIsSigned" />}
          subTitle={<Trans i18nKey="documentSign.titles.documentIsSignedInfo" />}
        />
      )}

      {url && !confirmEmailSignature.isSuccess && (
        <>
          <div className="left-align margin-bottom-tiny">
            <strong className={styles.greetings}>
              <Trans i18nKey="documentSign.helpers.greetings" />, {clientName},
            </strong>

            <Typography.Paragraph className={classNames(styles.text, "margin-top-tiny")}>
              <Trans i18nKey="documentSign.helpers.documentInfoHintBeforeConfirm" />
            </Typography.Paragraph>

            <Typography.Paragraph className={styles.text}>
              <Trans i18nKey="documentSign.helpers.documentInfoHintInCaseOfUnConfirm" />
            </Typography.Paragraph>

            <Typography.Paragraph className={styles.text}>
              {smsRequired ? (
                <Trans i18nKey="documentSign.helpers.smsDocumentInfoHint" values={{ documentName }} />
              ) : (
                <Trans i18nKey="documentSign.helpers.documentInfoHint" values={{ documentName }} />
              )}
            </Typography.Paragraph>

            <Typography.Paragraph className={styles.text}>
              <Trans i18nKey="documentSign.helpers.checkDocument" />
            </Typography.Paragraph>
          </div>

          <Card styles={{ body: { padding: 12 } }}>
            <iframe className={styles.documentIframe} src={url} width="100%" />
          </Card>

          <Form
            form={form}
            name="confirmDocumentSign"
            onFinish={(values) =>
              confirmEmailSignature.mutate(values, {
                onError: (error) => setErrorsToForm(form, "documentSign.confirmDocAttrs", error.violations),
              })
            }
          >
            <HiddenInput name="token" initialValue={token} />

            {smsRequired === "true" && (
              <Form.Item
                style={{ maxWidth: 300 }}
                className="margin-top-small margin-bottom-small left-align"
                name="smsCode"
                label={
                  <LabelWithTooltip
                    tooltip={t("documentSign.helpers.smsCodeHint")}
                    label={<Trans i18nKey="documentSign.confirmDocAttrs.smsCode" />}
                  />
                }
                rules={[validations.notNull, validations.length(6), validations.numeric]}
              >
                <Input placeholder={t("documentSign.helpers.yourSmsCode")} maxLength={6} showCount />
              </Form.Item>
            )}

            <div className="margin-top-small margin-bottom-small">
              {topAgentType === TopAgentType.PLUS ? (
                smsRequired ? (
                  <div className="left-align">
                    <div>
                      <Typography.Paragraph>
                        <Trans i18nKey="documentSign.helpers.smsDocumentDisagreement" />
                      </Typography.Paragraph>
                      <Trans i18nKey="documentSign.helpers.smsClickOnButton" />:
                      <ul
                        style={{ textDecoration: "unset", paddingLeft: 24, display: "block" }}
                        className="margin-top-tiny"
                      >
                        <li>
                          <Trans i18nKey="documentSign.helpers.smsConfirmContent" values={{ documentName }} />,
                        </li>
                        <li>
                          <Trans i18nKey="documentSign.helpers.smsConfirmSigning" values={{ documentName }} />,
                        </li>
                        <li>
                          <Trans i18nKey="documentSign.helpers.smsConfirmClientInformation" values={{ documentName }} />
                          ,
                        </li>
                        <li>
                          <Trans i18nKey="documentSign.helpers.smsConfirmDocumentation" />.
                        </li>
                        <li>
                          <Trans i18nKey="documentSign.helpers.smsConfirmNotFinancialMedationSigning" />.
                        </li>
                      </ul>
                    </div>

                    <p>
                      <Trans i18nKey="documentSign.helpers.smsConsentPLUS" values={{ documentName }} />.
                    </p>
                  </div>
                ) : (
                  <div className="left-align">
                    <div>
                      <Trans i18nKey="documentSign.helpers.clickOnButton" />:
                      <ul
                        style={{ textDecoration: "unset", paddingLeft: 24, display: "block" }}
                        className="margin-top-tiny"
                      >
                        <li>
                          <Trans i18nKey="documentSign.helpers.confirmFinancialMediation" />,
                        </li>
                        <li>
                          <Trans i18nKey="documentSign.helpers.confirmYourWill" />,
                        </li>
                        <li>
                          <Trans i18nKey="documentSign.helpers.confirmDocumentation" />.
                        </li>
                      </ul>
                    </div>

                    <p>
                      <Trans i18nKey="documentSign.helpers.capitalMarketConsent" />
                    </p>

                    <p>
                      <Trans i18nKey="documentSign.helpers.consentPLUS" />
                    </p>
                  </div>
                )
              ) : (
                <p className="margin-top-medium">
                  <Trans i18nKey="documentSign.helpers.consentMessage" />
                </p>
              )}
            </div>

            <Form.Item>
              <Button size="large" type="primary" htmlType="submit" loading={confirmEmailSignature.isPending}>
                <Trans i18nKey="documentSign.actions.sign" />
              </Button>
            </Form.Item>
          </Form>
        </>
      )}
    </ContentWrapper>
  );
};
